/**
 * Translation badge catalog — 15 collectible artifacts unlocked by lifetime
 * translation count. Ported from the mobile app's constants/badgeCatalog.ts
 * and i18n/locales/en.json (`badges.items.<id>.*`) so the copy matches
 * across platforms.
 */

export interface BadgeDefinition {
  id: string
  milestone: number
  color: string
  name: string
  subtitle: string
  lore: string
  achievement: string
  era: string
  toastSubtitle: string
  /** apps/web/public/progress/badges/<image>.webp */
  image: string
}

export const BADGE_CATALOG: BadgeDefinition[] = [
  {
    id: 'cheres',
    milestone: 1,
    color: '#a3e635',
    name: 'Cheres',
    subtitle: 'First Shard',
    lore: "Your journey begins with a single pottery fragment. Like ancient archaeologists piecing together history from broken vessels, you've discovered your first piece of Hebrew knowledge. Every great collection starts with one shard.",
    achievement: 'Completed Your First Translation',
    era: 'Bronze Age (3300-1200 BCE)',
    toastSubtitle: 'You Found Your First Shard!',
    image: '1',
  },
  {
    id: 'flint',
    milestone: 5,
    color: '#22c55e',
    name: 'Flint',
    subtitle: 'The Stone Tool',
    lore: "Flint tools were humanity's first technology, used for over 2 million years. You've now mastered the basics, sharp, practical, essential. Like ancient toolmakers, you're building skills that will serve you forever.",
    achievement: 'Completed 5 Translations',
    era: 'Stone Age (1.5M-3300 BCE)',
    toastSubtitle: 'The Stone Tool Discovered!',
    image: '2',
  },
  {
    id: 'masmer',
    milestone: 10,
    color: '#8b5cf6',
    name: 'Masmer',
    subtitle: 'The Nail',
    lore: 'Bronze nails held together ancient structures. Small but crucial, they connected wood and stone into lasting monuments. Your Hebrew skills are now becoming the nails that hold your understanding together.',
    achievement: 'Completed 10 Translations',
    era: 'Iron Age (1200-586 BCE)',
    toastSubtitle: 'The Nail Holds Strong!',
    image: '3',
  },
  {
    id: 'chetz',
    milestone: 25,
    color: '#3b82f6',
    name: 'Chetz',
    subtitle: 'The Arrow',
    lore: 'Arrowheads represent precision and purpose. Ancient warriors crafted each point with care, knowing their survival depended on accuracy. You are now striking targets with increasing precision in Hebrew.',
    achievement: 'Completed 25 Translations',
    era: 'Bronze Age (3300-1200 BCE)',
    toastSubtitle: 'Swift As An Arrow!',
    image: '4',
  },
  {
    id: 'ostracon',
    milestone: 50,
    color: '#f97316',
    name: 'Ostracon',
    subtitle: 'First Writing',
    lore: "Ostraca, pottery shards used as writing surfaces, preserve some of history's most intimate messages. Letters between soldiers, shopping lists, prayers. You've now written your mark in Hebrew history.",
    achievement: 'Completed 50 Translations',
    era: 'First Temple Period (960-586 BCE)',
    toastSubtitle: 'Your First Writing Unearthed!',
    image: '5',
  },
  {
    id: 'chotem',
    milestone: 100,
    color: '#ec4899',
    name: 'Chotem',
    subtitle: 'The Seal',
    lore: 'Clay seals bore the mark of ancient officials, authenticating documents and securing property. Your Hebrew knowledge now carries the seal of authenticity, you have proven your dedication.',
    achievement: 'Completed 100 Translations',
    era: 'Iron Age (1200-586 BCE)',
    toastSubtitle: 'The Seal Of Authenticity!',
    image: '6',
  },
  {
    id: 'matbea',
    milestone: 200,
    color: '#10b981',
    name: "Matbe'a",
    subtitle: 'The Coin',
    lore: 'Ancient Judean coins bore symbols of freedom, pomegranates, vines, chalices. Each coin told a story of independence. Your Hebrew mastery is now currency, valuable, exchangeable, recognized.',
    achievement: 'Completed 200 Translations',
    era: 'Hasmonean Period (140-37 BCE)',
    toastSubtitle: 'Ancient Coin Discovered!',
    image: '7',
  },
  {
    id: 'tzlochit',
    milestone: 350,
    color: '#eab308',
    name: 'Tzlochit',
    subtitle: 'The Vessel',
    lore: 'Glass vessels were precious in antiquity, colored amphorae that held perfumes, oils, wine. You have become a vessel too, holding and preserving Hebrew knowledge with growing capacity.',
    achievement: 'Completed 350 Translations',
    era: 'Roman Period (63 BCE-324 CE)',
    toastSubtitle: 'A Precious Vessel Revealed!',
    image: '8',
  },
  {
    id: 'tabaat',
    milestone: 500,
    color: '#f59e0b',
    name: "Taba'at",
    subtitle: 'The Ring',
    lore: 'Signet rings bore personal seals, symbols of identity and authority. Kings and merchants alike pressed their rings into wax to mark ownership. Hebrew is now part of your identity.',
    achievement: 'Completed 500 Translations',
    era: 'Second Temple Period (516 BCE-70 CE)',
    toastSubtitle: 'The Signet Ring Is Yours!',
    image: '9',
  },
  {
    id: 'scarab',
    milestone: 750,
    color: '#06b6d4',
    name: 'Scarab',
    subtitle: 'The Amulet',
    lore: 'Scarab amulets protected their wearers, carved with prayers and protective symbols. Rare and sacred, they were passed down through generations. Your Hebrew knowledge is now a protection, a connection to something ancient.',
    achievement: 'Completed 750 Translations',
    era: 'Egyptian Influence (1550-1070 BCE)',
    toastSubtitle: 'The Sacred Amulet Found!',
    image: '10',
  },
  {
    id: 'shekel',
    milestone: 1000,
    color: '#fbbf24',
    name: 'Shekel',
    subtitle: 'The Silver',
    lore: 'The Tyrian shekel, silver currency that funded the Temple itself. Pilgrims traveled from across the world bearing these coins. You have reached a milestone of true value. 1,000 translations mark you as seriously dedicated.',
    achievement: 'Completed 1,000 Translations',
    era: 'Second Temple Period (516 BCE-70 CE)',
    toastSubtitle: 'Silver Of Great Value!',
    image: '11',
  },
  {
    id: 'pesel',
    milestone: 1500,
    color: '#dc2626',
    name: 'Pesel',
    subtitle: 'The Figurine',
    lore: 'Bronze lion figurines adorned palaces and temples, symbols of strength, courage, royal power. You have earned the strength of the lion. Your Hebrew prowess is now formidable.',
    achievement: 'Completed 1,500 Translations',
    era: 'Kingdom Period (1050-586 BCE)',
    toastSubtitle: 'The Bronze Lion Roars!',
    image: '12',
  },
  {
    id: 'agil',
    milestone: 2500,
    color: '#f472b6',
    name: 'Agil',
    subtitle: 'The Earring',
    lore: 'Gold crescent earrings were worn by the elite, priests, nobles, royalty. Intricate and beautiful, they represented refined taste and wealth. Your Hebrew skills are now refined to a golden standard.',
    achievement: 'Completed 2,500 Translations',
    era: 'Late Bronze Age (1550-1200 BCE)',
    toastSubtitle: 'Gold Crescent Shines Bright!',
    image: '13',
  },
  {
    id: 'malki',
    milestone: 5000,
    color: '#a855f7',
    name: 'Malki',
    subtitle: 'Royal Seal',
    lore: "Amethyst gems carved with royal insignias, the mark of kings and high priests. These purple stones adorned the Hoshen, the High Priest's breastplate. You have achieved royal status in Hebrew mastery.",
    achievement: 'Completed 5,000 Translations',
    era: 'Kingdom Period (1050-586 BCE)',
    toastSubtitle: 'Royal Seal Of Amethyst!',
    image: '14',
  },
  {
    id: 'keter',
    milestone: 10000,
    color: '#991b1b',
    name: 'Keter',
    subtitle: 'The Crown',
    lore: 'The golden diadem, ultimate symbol of kingship, authority, and achievement. Found in royal tombs, these crowns represented divine right and earthly power. You wear the crown of Hebrew mastery. 10,000 translations mark you as legendary.',
    achievement: 'Completed 10,000 Translations',
    era: 'Kingdom Period (1050-586 BCE)',
    toastSubtitle: 'The Golden Crown Is Yours!',
    image: '15',
  },
]

/** Highest badge whose milestone the count has reached, or null if none yet. */
export function badgeForCount(totalTranslations: number): BadgeDefinition | null {
  let highest: BadgeDefinition | null = null
  for (const badge of BADGE_CATALOG) {
    if (totalTranslations >= badge.milestone) highest = badge
  }
  return highest
}

/** Next locked badge above the current count, or null once all are unlocked. */
export function nextBadge(totalTranslations: number): BadgeDefinition | null {
  return BADGE_CATALOG.find((b) => b.milestone > totalTranslations) ?? null
}

export function badgeImageUrl(badge: Pick<BadgeDefinition, 'image'>): string {
  return `/progress/badges/${badge.image}.webp`
}

/**
 * Returns the newly-crossed milestone, if `newCount` passed one that
 * `previousCount` had not, else null. Mirrors the mobile app's
 * checkCelebrationMilestone.
 */
export function crossedBadgeMilestone(newCount: number, previousCount: number): BadgeDefinition | null {
  for (const badge of BADGE_CATALOG) {
    if (newCount >= badge.milestone && previousCount < badge.milestone) return badge
  }
  return null
}


/**
 * The badge being worked towards, and how far along the run to it is.
 *
 * The Badges page could say what a locked badge needed but never what you
 * had, so "unlock at 500" meant nothing without knowing you were at 60. One
 * helper so the rail card, the Badges header and the locked-badge detail all
 * count the same way: progress is measured from the PREVIOUS milestone, not
 * from zero, or every bar after the first one looks nearly full.
 */
export interface BadgeProgress {
  next: BadgeDefinition | null
  /** Translations still needed for `next` (0 when every badge is unlocked). */
  remaining: number
  /** 0-100 across the gap between the last milestone and the next one. */
  pct: number
  unlockedCount: number
}

export function badgeProgress(totalTranslations: number): BadgeProgress {
  const unlocked = BADGE_CATALOG.filter((b) => totalTranslations >= b.milestone)
  const next = BADGE_CATALOG.find((b) => totalTranslations < b.milestone) ?? null
  if (!next) {
    return { next: null, remaining: 0, pct: 100, unlockedCount: unlocked.length }
  }
  const floor = unlocked.length > 0 ? unlocked[unlocked.length - 1].milestone : 0
  const span = Math.max(1, next.milestone - floor)
  const done = Math.min(span, Math.max(0, totalTranslations - floor))
  return {
    next,
    remaining: Math.max(0, next.milestone - totalTranslations),
    pct: Math.round((done / span) * 100),
    unlockedCount: unlocked.length,
  }
}
